import React from 'react';
import DataTable from '../../../components/DataTable';
import { normalizeTimestamp, formatValue } from './chartUtils';
import WidgetState from './WidgetState';

const EmptyState = () => (
  <WidgetState isEmpty />
);

const TableWidget = ({ data, config = {} }) => {
  // Table responses carry columns/rows at the TOP level (viewer/preview merge);
  // data.data may legitimately be [] for them — don't gate on it.
  if (!data || (!data.data && !data.rows)) {
    return <EmptyState />;
  }

  // Handle different data formats
  if (data.type === 'table') {
    // Accept both top-level (current merge shape) and legacy nested placement —
    // the nested-only read left real table results rendering blank.
    const columns = data.columns || data.data?.columns || [];
    const rows = data.rows || data.data?.rows || [];
    return renderTable(columns, rows, config);
  } else if (data.type === 'timeseries') {
    // Convert timeseries data to table format
    return renderTimeseriesTable(data.data, config);
  } else if (data.type === 'histogram') {
    // Convert histogram data to table format
    const columns = ['Label', 'Value'];
    const rows = data.data.map(item => [item.label, item.value]);
    return renderTable(columns, rows, config);
  } else if (data.type === 'scalar') {
    // Render single value in a table
    return renderTable(['Value'], [[data.data]], config);
  } else {
    return <EmptyState />;
  }
};

// Render table with given columns and rows
function renderTable(columns, rows, config) {
  const tableColumns = columns.map((col, index) => ({
    title: col,
    dataIndex: `col${index}`,
    key: `col${index}`,
    // Route numbers through formatValue so unit + decimalPlaces are honoured.
    render: (text) => {
      if (typeof text === 'number') {
        return formatValue(text, config);
      }
      return text;
    }
  }));

  // Rows arrive in TWO shapes and this must accept both:
  //  - objects keyed by column name — what the SQL endpoint emits
  //    (DashboardSQLExecutionService puts a List<Map<String,Object>> in "rows"),
  //    which is every real table widget;
  //  - positional arrays — what the histogram/scalar branches above construct.
  // This used to call row.forEach() unconditionally, so an object row threw
  // "row.forEach is not a function" and, with no per-widget error boundary,
  // took the whole dashboard down with it. (utils/exportWidgetCsv.js already
  // normalised both shapes; this is the same mapping.)
  const tableData = rows.map((row, rowIndex) => {
    const rowData = { key: `row${rowIndex}` };
    if (Array.isArray(row)) {
      row.forEach((cell, cellIndex) => {
        rowData[`col${cellIndex}`] = cell;
      });
    } else if (row && typeof row === 'object') {
      columns.forEach((col, cellIndex) => {
        rowData[`col${cellIndex}`] = row[col];
      });
    } else {
      // Scalar row (a bare value) — render it in the first column.
      rowData.col0 = row;
    }
    return rowData;
  });

  return <DataTable columns={tableColumns} data={tableData} size="small" />;
}

// Convert timeseries data to table format
function renderTimeseriesTable(timeseriesData, config) {
  // Collect all unique timestamps — normalize to epoch ms BEFORE dedup so that
  // seconds/millis mixes collapse to one key and the sort below is numeric.
  const timestamps = new Set();
  const seriesLookups = timeseriesData.map(series => {
    const lookup = new Map();
    series.values.forEach(point => {
      const ts = normalizeTimestamp(point[0]);
      timestamps.add(ts);
      lookup.set(ts, point[1]);
    });
    return lookup;
  });

  // Numeric sort — a lexicographic sort misorders timestamps of differing
  // digit counts (e.g. "9..." before "10...").
  const sortedTimestamps = Array.from(timestamps).sort((a, b) => a - b);

  // Create columns: Timestamp + one column per series
  const columns = ['Timestamp'];
  timeseriesData.forEach(series => {
    let seriesName = 'Series';

    // Try to get a meaningful name from the metric labels
    if (series.metric) {
      const labels = Object.entries(series.metric)
        .filter(([key]) => !key.startsWith('__'))
        .map(([key, value]) => `${key}="${value}"`)
        .join(', ');

      if (labels) {
        seriesName = labels;
      } else if (series.metric.__name__) {
        seriesName = series.metric.__name__;
      }
    }

    columns.push(seriesName);
  });

  // Create rows
  const rows = sortedTimestamps.map(timestamp => {
    const row = [new Date(timestamp).toLocaleString()];

    seriesLookups.forEach(lookup => {
      const v = lookup.get(timestamp);
      row.push(v === undefined ? null : v);
    });

    return row;
  });

  // Cap the rendered rows so a long window doesn't build a giant DOM table.
  // Say so when it happens — silently dropping rows reads as "this is all the
  // data", which is how a truncated table gets mistaken for a complete one.
  const MAX_TIMESERIES_ROWS = 50;
  const truncated = rows.length > MAX_TIMESERIES_ROWS;
  const recentRows = truncated ? rows.slice(-MAX_TIMESERIES_ROWS) : rows;

  return (
    <>
      {truncated && (
        <div style={{ color: '#6b7280', fontSize: '0.75rem', padding: '2px 4px' }}>
          Showing the most recent {MAX_TIMESERIES_ROWS} of {rows.length} points
        </div>
      )}
      {renderTable(columns, recentRows, config)}
    </>
  );
}

export default TableWidget;
